import React, { useState, useEffect } from 'react';
import { Lock, Unlock } from 'lucide-react';
import FreightDestinationSelect from '../../../../shared/FreightDestinationSelect';

export default function LineItemTrade({ it, updateItem, readOnly = false, freightDestinations = [] }) {
  const [tradeInput, setTradeInput] = useState('');
  const [qtyInput, setQtyInput] = useState('');
  const [shippingInput, setShippingInput] = useState('');
  const [tariffInput, setTariffInput] = useState('');
  const [taxInput, setTaxInput] = useState('');

  /* ---------------------------------------------------
     SYNC FROM DATA
  --------------------------------------------------- */
  useEffect(() => {
    setTradeInput(it.tradePrice === 0 ? '' : String(it.tradePrice ?? ''));
    setQtyInput(it.qty === 0 ? '' : String(it.qty ?? ''));
    setShippingInput(it.shipping === 0 ? '' : String(it.shipping ?? ''));
    setTariffInput(it.tariff === 0 ? '' : String(it.tariff ?? ''));
    setTaxInput(it.prepaidTax === 0 ? '' : String(it.prepaidTax ?? ''));
  }, [it.tradePrice, it.qty, it.shipping, it.tariff, it.prepaidTax]);

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const fmt2 = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : '0.00';
  };

  const shippingFromCatalog = Number.isFinite(it.unitShipping);
  const tariffFromCatalog = Number.isFinite(it.unitTariff);
  const shippingLocked = shippingFromCatalog && !it.shippingUnlocked;
  const tariffLocked = tariffFromCatalog && !it.tariffUnlocked;

  const handleQtyBlur = () => {
    const newQty = toNum(qtyInput);
    const patch = { qty: newQty };
    if (shippingLocked) patch.shipping = Number(it.unitShipping) * newQty;
    if (tariffLocked) patch.tariff = Number(it.unitTariff) * newQty;
    updateItem(it.itemId, patch);
  };

  const unlockShipping = () => updateItem(it.itemId, { shippingUnlocked: true });
  const relockShipping = () =>
    updateItem(it.itemId, {
      shippingUnlocked: false,
      shipping: Number(it.unitShipping) * toNum(it.qty),
    });

  const unlockTariff = () => updateItem(it.itemId, { tariffUnlocked: true });
  const relockTariff = () =>
    updateItem(it.itemId, {
      tariffUnlocked: false,
      tariff: Number(it.unitTariff) * toNum(it.qty),
    });

  return (
    <div className="li-col li-trade">
      {/* Trade Price */}
      <label>Trade Price</label>
      <div className="if-currency">
        <span className="if-currency-prefix">$</span>
        <input
          className="if-input"
          type="number"
          step="0.01"
          value={tradeInput}
          disabled={readOnly}
          onChange={(e) => setTradeInput(e.target.value)}
          onFocus={(e) => {
            if (e.target.value === '0' || e.target.value === '0.00') {
              setTradeInput('');
            }
          }}
          onBlur={() => updateItem(it.itemId, { tradePrice: toNum(tradeInput) })}
        />
      </div>

      {/* Qty */}
      <label>Qty</label>
      <input
        className="if-input"
        type="number"
        step="1"
        value={qtyInput}
        disabled={readOnly}
        onChange={(e) => setQtyInput(e.target.value)}
        onFocus={(e) => {
          if (e.target.value === '0') {
            setQtyInput('');
          }
        }}
        onBlur={handleQtyBlur}
      />

      {/* Unit Type */}
      <label>Unit Type</label>
      <select
        className="if-select"
        value={it.unitType || 'Each'}
        disabled={readOnly}
        onChange={(e) => updateItem(it.itemId, { unitType: e.target.value })}
      >
        <option value="Each">Each</option>
        <option value="Yards">Yards</option>
        <option value="Feet">Feet</option>
        <option value="Sq Ft">Sq Ft</option>
        <option value="Sq Yards">Sq Yards</option>
        <option value="Rolls">Rolls</option>
      </select>

      {/* Trade Shipping */}
      <div className="li-field-label-row">
        <label>Shipping (Trade, Per Order)</label>
        {shippingFromCatalog && !readOnly && (
          <button
            type="button"
            className="li-catalog-lock-btn"
            title={
              shippingLocked
                ? 'Locked to catalog shipping rate — click to override'
                : 'Overridden — click to reset to catalog shipping rate'
            }
            onClick={shippingLocked ? unlockShipping : relockShipping}
          >
            {shippingLocked ? <Lock size={12} /> : <Unlock size={12} />}
          </button>
        )}
      </div>
      {shippingLocked ? (
        <div className="if-currency readonly">
          <span className="if-currency-prefix">$</span>
          <input className="if-input" type="text" value={fmt2(it.shipping)} disabled />
        </div>
      ) : (
        <div className="if-currency">
          <span className="if-currency-prefix">$</span>
          <input
            className="if-input"
            type="number"
            step="0.01"
            value={shippingInput}
            disabled={readOnly}
            onChange={(e) => setShippingInput(e.target.value)}
            onFocus={(e) => {
              if (e.target.value === '0' || e.target.value === '0.00') {
                setShippingInput('');
              }
            }}
            onBlur={() => updateItem(it.itemId, { shipping: toNum(shippingInput) })}
          />
        </div>
      )}

      <label>Freight Destination</label>
      <FreightDestinationSelect
        className="if-select"
        value={it.shippingDestination || 'designer_warehouse'}
        address={it.shippingDestinationAddress}
        disabled={readOnly}
        freightDestinations={freightDestinations}
        onChange={(value, address) =>
          updateItem(it.itemId, { shippingDestination: value, shippingDestinationAddress: address })
        }
      />

      {/* Trade Tariff */}
      <div className="li-field-label-row">
        <label>Tariff (Trade, Per Order)</label>
        {tariffFromCatalog && !readOnly && (
          <button
            type="button"
            className="li-catalog-lock-btn"
            title={
              tariffLocked
                ? 'Locked to catalog tariff rate — click to override'
                : 'Overridden — click to reset to catalog tariff rate'
            }
            onClick={tariffLocked ? unlockTariff : relockTariff}
          >
            {tariffLocked ? <Lock size={12} /> : <Unlock size={12} />}
          </button>
        )}
      </div>
      {tariffLocked ? (
        <div className="if-currency readonly">
          <span className="if-currency-prefix">$</span>
          <input className="if-input" type="text" value={fmt2(it.tariff)} disabled />
        </div>
      ) : (
        <div className="if-currency">
          <span className="if-currency-prefix">$</span>
          <input
            className="if-input"
            type="number"
            step="0.01"
            value={tariffInput}
            disabled={readOnly}
            onChange={(e) => setTariffInput(e.target.value)}
            onFocus={(e) => {
              if (e.target.value === '0' || e.target.value === '0.00') {
                setTariffInput('');
              }
            }}
            onBlur={() => updateItem(it.itemId, { tariff: toNum(tariffInput) })}
          />
        </div>
      )}

      {/* Prepaid Sales Tax */}
      <label>Prepaid Sales Tax</label>
      <div className="if-currency">
        <span className="if-currency-prefix">$</span>
        <input
          className="if-input"
          type="number"
          step="0.01"
          value={taxInput}
          disabled={readOnly}
          onChange={(e) => setTaxInput(e.target.value)}
          onFocus={(e) => {
            if (e.target.value === '0' || e.target.value === '0.00') {
              setTaxInput('');
            }
          }}
          onBlur={() => updateItem(it.itemId, { prepaidTax: toNum(taxInput) })}
        />
      </div>
    </div>
  );
}
